import { useQuery, useQueryClient } from "@tanstack/solid-query";

import { useClient } from "@revolt/client";

import env from "./env";

/**
 * Custom theming, stored on this instance's sidecar service.
 * Server themes are owner-set and visible to every member;
 * user themes decorate profiles.
 */
export type CustomTheme = {
  /** CSS background for the chat area (colour or gradient) */
  background?: string;
  /** CSS background for profile cards */
  profile?: string;
};

/**
 * Reactive accessor for a server's or user's custom theme
 */
export function useCustomTheme(
  scope: "server" | "user",
  id: () => string | undefined,
) {
  const client = useClient();

  const query = useQuery<CustomTheme>(() => ({
    queryKey: ["customTheme", scope, id() ?? ""],
    enabled: !!id(),
    staleTime: 60_000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    queryFn: () => {
      const [authHeader, authHeaderValue] = client()!.authenticationHeader;
      return fetch(`${env.DEFAULT_GIFBOX_URL}/theme/${scope}/${id()}`, {
        headers: { [authHeader]: authHeaderValue },
      }).then((r) => (r.ok ? r.json() : {}));
    },
  }));

  return () => (query.data ?? {}) as CustomTheme;
}

/**
 * Hook providing a save function for a theme (owner / self only,
 * enforced server-side). Invalidate so every open view updates.
 */
export function useSaveCustomTheme() {
  const client = useClient();
  const queryClient = useQueryClient();

  return async (scope: "server" | "user", id: string, theme: CustomTheme) => {
    const [authHeader, authHeaderValue] = client()!.authenticationHeader;
    const res = await fetch(`${env.DEFAULT_GIFBOX_URL}/theme/${scope}/${id}`, {
      method: "PUT",
      headers: {
        [authHeader]: authHeaderValue,
        "content-type": "application/json",
      },
      body: JSON.stringify(theme),
    });
    if (!res.ok) throw new Error(`theme save failed: ${res.status}`);
    queryClient.invalidateQueries({ queryKey: ["customTheme", scope, id] });
  };
}
